import { Controller } from "@hotwired/stimulus"

// Load-everything coordination for the provider-search "View results"
// drawer. Chunk loading itself is handled by lazily loading Turbo Frames
// (providers/_search_results_next_page_frame), which chain until the full
// result set is rendered. This controller only eagerly loads any remaining
// frames on request: pdf-export:prepare (PDF captures must include every
// provider card) and provider-search-map:load-cards (a map marker
// interaction targeting a card on a not-yet-loaded page).
export default class extends Controller {
  // Invoked via pdf-export:prepare@window (a PDF capture waits for every
  // remaining chunk to load first) and provider-search-map:load-cards@window
  // (a marker interaction needs its card in the DOM); both pass the same
  // { pending, target } detail. Only reacts when this drawer's results are
  // inside the requesting target — an export of an unrelated element (e.g. a
  // provider-details drawer stacked above the results) must not trigger, wait
  // on, or be aborted by this drawer's frame loading.
  loadAll(event) {
    const target = event?.detail?.target
    if (target && !target.contains(this.element)) return
    const promise = this.loadRemaining()
    if (Array.isArray(event?.detail?.pending)) event.detail.pending.push(promise)
    return promise
  }

  // Rejects when a frame fails to load or settles without rendering, so the
  // pdf-export controller aborts instead of capturing a partial result set.
  async loadRemaining() {
    for (;;) {
      const frame = this.nextPendingFrame()
      if (!frame) return
      // Rewriting `loading` on an already-eager frame (e.g. an in-flight
      // prime-pricing frame) makes Turbo re-fetch it; only lazy frames need
      // the flip, in-flight eager ones just need to be awaited.
      if (frame.getAttribute("loading") === "lazy") frame.loading = "eager"
      await frame.loaded
      if (!this.frameLoaded(frame)) {
        throw new Error(`Provider result frame ${frame.id} did not complete loading`)
      }
    }
  }

  nextPendingFrame() {
    return [...this.element.querySelectorAll("turbo-frame[src]")].find((frame) => !this.frameLoaded(frame))
  }

  // Turbo marks a frame with the `complete` attribute once its response has
  // rendered. The `complete` property is NOT equivalent — it is `!isLoading`,
  // so a lazy frame that was never requested also reports `complete === true`.
  frameLoaded(frame) {
    return frame.hasAttribute("complete")
  }
}
